import type { ValueOf } from '@/lib/utils';
import type {
  AssignedTask as AssignedTaskRegistry,
  User as UserRegistry,
  VehicleListing as VehicleListingRegistry,
} from '@/models/registry';
import type { JSONContent } from '@tiptap/core';
import type { CustomerType } from '.';

export type Role = string;

export type User = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: Role;
  avatar: string;
  slack_connected?: boolean;
  username: string;
  slack_id?: string;
  slack_access_token?: string;
};

export type Hub = {
  id: string;
  name: string;
  path_identifier: string;
  code: string;
};

export type Address = {
  id: string;
  customer_id: string;
  address_type: 'registration' | 'delivery';
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
};

export type ArchivedDeal = CoreDeal & {
  lender: Lender;
  hub: Hub;
  created_by: User;
  finance_manager: User;
  primary_sales_rep: User;
  secondary_sales_rep: User;
  payments: Payment[];
  price_breakdown: PriceBreakdown;
  customers: CustomerExtended[];
  protection_plan: ProtectionPlan;
  // vehicle: Vehicle;
  files: FileMetadata[];
  deal_task_assignments: AssignedTask[];
  audits: Audit[];
  comments: Comment[];
  emoji_reactions: EmojiReaction[];
  confirmations: ConfirmationExtended[];
  tasks: TaskV2[];
};

type CustomerExtended = Customer & {
  registration_address: Address;
  delivery_address: Address | null;
};

export type CoreDeal = {
  id: string;
  vehicle_listing_id: string;
  created_at: string;
  last_updated: string;
  archived_at: string | null;
  short_id: string;
  status: Status;
  stage: Stage;

  cdk_id: number;
  hub_path_identifier: string;
  delivery_preference: DeliveryPreference;
  delivery_date: string | null;
  pickup_date: string | null;
  date_added_to_customer_delivery_queue: string | null;
  favorited: boolean;
  priority: Priority;
  funding_method: FundingMethod;
  risk: DealRisk | null;
  ai_summary:
    | {
        bullet: string;
        date_time: string;
        subject: string;
      }[]
    | null;
  sms_conversation: string[] | null;
  agent_convo_paused: boolean;
  vehicle_delivery_odometer: number | null;
  rapid_recon_step: RapidReconStep | null;
  vehicle_current_location_id: string | null;
  delivery_stage: DeliveryStage | null;
  sms_flow: string | null;
  sms_flow_step: string | null;
  // assignees
  finance_manager_id?: string | null;
  primary_sales_rep_id?: string | null;
  secondary_sales_rep_id?: string | null;
  lender_id?: string | null;
};

export type Deal = {
  id: string;
  assigned_tasks: AssignedTask[];
  audit_log: AuditLog[];
  short_id: string;
  hub_path_identifier: string;
  cdk_id: number;
  vehicle_listing: VehicleListing | null;
  delivery_preference: DeliveryPreference;
  delivery_date: string | null;
  pickup_date: string | null;
  date_added_to_customer_delivery_queue: string | null;
  favorited: boolean;
  files: FileMetadata[];
  funding_method: FundingMethod;
  lender: Lender | null;
  protection_plan: ProtectionPlan | null;
  priority: Priority;
  created_at: string;
  finance_manager: FinanceManager | null;
  primary_sales_rep: SalesRep | null;
  secondary_sales_rep: SalesRep | null;
  stage: Stage;
  status: Status;
  risk: DealRisk | null;
  price_breakdown: PriceBreakdown | null;
  trades: Trade[];
  audits: Audit[];
  comments: Comment[];
  primary_customer: Customer | null;
  co_registrant: Customer | null;
  last_updated: string;
  payments: Payment[];
};

export type Customer =
  | {
      id: string;
      deal_id: string;
      customer_type: CustomerType;
      full_name: string;
      email: string;
      mobile_number: string;
      // registration_address: Address;
      // delivery_address: Address | null;
    }
  | {
      id: string;
      deal_id: string;
      customer_type: CustomerType;
      full_name: string;
      email: string;
      mobile_number: string;
      // registration_address: Address;
      // delivery_address: null;
    };

export type RapidReconStep =
  | 'Purchase/Auction'
  | 'Listing + DB Input'
  | 'In Transit'
  | 'Vehicle Hold'
  | 'Offsite Repairs'
  | 'Intake Inspection'
  | 'One Shot Photo'
  | 'Battery Diagnostics'
  | 'Full Inspection'
  | 'Order Parts'
  | 'Parts Arrived'
  | 'Mechanical Repair'
  | 'Ready For Pickup'
  | 'Body Shop'
  | 'Cosmetic'
  | 'Wheels'
  | 'Paintless Dent Repair'
  | 'Detail'
  | 'Full Photos'
  | 'Ready For Sale'
  | 'Wholesale'
  | 'Needs Attention'
  | 'None';

export const RapidReconSteps: RapidReconStep[] = [
  'Purchase/Auction',
  'Listing + DB Input',
  'In Transit',
  'Vehicle Hold',
  'Offsite Repairs',
  'Intake Inspection',
  'One Shot Photo',
  'Battery Diagnostics',
  'Full Inspection',
  'Order Parts',
  'Parts Arrived',
  'Mechanical Repair',
  'Ready For Pickup',
  'Body Shop',
  'Cosmetic',
  'Detail',
  'Full Photos',
  'Ready For Sale',
  'Wholesale',
  'Needs Attention',
  'None',
];

export type DeliveryStage =
  | 'none'
  | 'pending_pickup'
  | 'in_delivery'
  | 'delivered';

export const DeliveryStages: DeliveryStage[] = [
  'none',
  'pending_pickup',
  'in_delivery',
  'delivered',
];

export type ResellType = 'retail' | 'wholesale' | 'none';

export type TradeIn = {
  id: string;
  deal_id: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  condition: string;
  paid_off: boolean;
  lienholder: string;
  state: string;
  postal_code: string;
  number_of_keys: number;
  asking_price: number;
  exterior_color: string;
  interior_color: string;
  offer: number;
  resell_type: ResellType;
};

export type TradeCurrentLocation = 'customer' | 'transit' | 'hq' | 'auction';

export type Trade = {
  id: string;
  short_id: string;
  created_at: string;
  updated_at: string;
  created_by_id: string;
  deal_id: string;
  full_name: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  condition: string;
  paid_off: boolean;
  lienholder: string;
  state: string;
  postal_code: string;
  number_of_keys: number;
  asking_price: number;
  exterior_color: string;
  interior_color: string;
  offer: number;
  resell_type: ResellType;
  status: TradeStatus;
  title_present: boolean;
  payoff_amount: number;
  payoff_expiration: string | null;
  appraised_value: number;
  ever_location: string;
  current_location: TradeCurrentLocation;
  auctions: Auction['id'][] | null; // Array of auction IDs
};

export type Lender = {
  id: string;
  name: string;
  email: string;
  phone: string;
  lender_type: 'bank' | 'credit_union';
};

export type PriceBreakdown = {
  id: string;
  deal_id: string;
  subtotal: number;
  total: number;
  sales_tax: number;
  registration_fee: number;
  documentation_fee: number;
  title_fee: number;
  delivery_fee: number;
  locked: boolean;
};

export type Audit = {
  id: string;
  deal_id: string;
  created_at: string;
  user_id: string;
  field: string;
  old_value: string;
  new_value: string;
  message: string;
};

export type FundingMethod = 'cash' | 'loan' | 'outside_financing' | 'none';

export const FundingMethods: FundingMethod[] = [
  'cash',
  'loan',
  'outside_financing',
  'none',
];

export type Status =
  | 'draft'
  | 'new'
  | 'in_progress'
  | 'awaiting_delivery'
  | 'delivered'
  | 'done'
  | 'cancelled'
  | 'returning'
  | 'returned';

export const Statuses: Status[] = [
  'draft',
  'new',
  'in_progress',
  'awaiting_delivery',
  'delivered',
  'done',
  'cancelled',
  'returning',
  'returned',
];

export enum ConfirmationField {
  CustomerName = 'primary_customer.full_name',
  CustomerEmail = 'primary_customer.email',
  CustomerPhoneNumber = 'primary_customer.phone_number',
  CustomerRegistrationAddress = 'primary_customer.registration_address',
  CustomerDeliveryAddress = 'primary_customer.delivery_address',
  CoRegistrantName = 'co_registrant.full_name',
  CoRegistrantEmail = 'co_registrant.email',
  CoRegistrantPhoneNumber = 'co_registrant.phone_number',
  CoRegistrantRegistrationAddress = 'co_registrant.registration_address',
  Vin = 'vehicle.vin',
  Odometer = 'vehicle.odometer',
  TradeInYMM = 'trade_in.ymm',
  RapidReconStatus = 'vehicle.rapid_recon_step',
  VehicleCurrentLocation = 'vehicle.current_location',
  DeliveryStage = 'vehicle.delivery_stage',
}

export type ConfirmationHistoryEntry = {
  id: string;
  user_id: string;
  confirmation_id: string;
  value: string;
  created_at: string;
};

export type Confirmation = {
  id: string;
  confirmation_to_id: string;
  confirmation_to_type: ConfirmationTargetType;
  field_key: ConfirmationField;
  confirmed: boolean;
  most_recent_entry_id: string;
  is_manually_toggled: boolean;
};

export type ConfirmationExtended = Confirmation & {
  confirmation_history: ConfirmationHistoryEntry[];
};

export enum ConfirmationTargetType {
  Deal = 'deal',
  Trade = 'trade',
}

export type EmojiReaction = {
  id: string;
  reaction_to_id: string;
  reaction_to_type: EmojiReactionType;
  created_at: string;
  user_id: string;
  emoji: string;
};

export enum EmojiReactionType {
  Deal = 'deal',
  Comment = 'comment',
  Reply = 'reply',
  Trade = 'trade',
}

export type EmojiReactionSummary = {
  emoji: string;
  hasUserReacted: boolean;
  usersReacted: string[];
};

export type EmojiReactionPayload = {
  id: string;
  emoji: string;
  targetId: string;
  targetType: EmojiReactionType;
  userId: string;
};

export enum EmojiReactionAction {
  Add = 'add',
  Remove = 'remove',
}

export type EmojiReactionSummaryMap = Record<string, EmojiReactionSummary>;

export type Comment = {
  id: string;
  commentable_id: string;
  commentable_type: CommentTargetType;
  created_at: string;
  updated_at: string;
  user_id: string;
  content: JSONContent;
  text: string;
  edited: boolean;
  replies: Reply[];
  // mentions: User['id'][];
};

export enum CommentTargetType {
  Deal = 'deal',
  Trade = 'trade',
  Appraisal = 'appraisal',
}

export const CommentsOrderBy = {
  Newest: 'newest',
  Oldest: 'oldest',
} as const;

export type CommentsOrderBy = ValueOf<typeof CommentsOrderBy>;

export type Reply = {
  id: string;
  comment_id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  content: JSONContent;
  edited: boolean;
  text: string;
  mentions: User['id'][];
};

export type Priority = 'low' | 'medium' | 'high' | 'none';

export const Priorities: Priority[] = ['low', 'medium', 'high', 'none'];

export type TaskTemplate = {
  id: string;
  title: string;
  description: string;
  taskable_type: TaskableType;
};

export const TaskableType = {
  Deal: 'deal',
  Appraisal: 'appraisal',
} as const;
export type TaskableType = ValueOf<typeof TaskableType>;

export const AssigneeType = {
  User: 'user',
  ProductUser: 'product_user',
} as const;
export type AssigneeType = ValueOf<typeof AssigneeType>;

export const TaskStatus = {
  Incomplete: 'incomplete',
  Done: 'done',
} as const;
export type TaskStatus = ValueOf<typeof TaskStatus>;

export type TaskV2 = {
  id: string;
  task_template_id?: string | null;
  taskable_id: string;
  taskable_type: TaskableType;
  title: string;
  description: string;
  status: TaskStatus;
  assignee_id?: string | null;
  assignee_type?: AssigneeType | null;
  assignee?: User | null;
  product_user_id?: string | null;
};

export type Task = {
  id: string;
  title: string;
  description: string;
};

export type AssignedTask = {
  id: string;
  task_id: string;
  deal_id: string;
  title: string;
  description: string;
  status: 'incomplete' | 'done';
  assignee_id?: string | null;
  assignee?: User | null;
};

export type AuditLog = {
  id: string;
  deal_id: string;
  type: 'audit' | 'comment';
};

export type DeliveryPreference =
  | 'none'
  | 'pickup'
  | 'local_delivery'
  | 'extended'
  | 'delivery';

export const DeliveryPreferences: DeliveryPreference[] = [
  'none',
  'pickup',
  'local_delivery',
  'extended',
  'delivery',
];

export type FinanceManager = User;
export type SalesRep = User;

export type DealRisk = {
  level: 'low' | 'needs_attention' | 'high';
  checks_failed: string[];
};

export type FileMetadata = {
  id: string;
  deal_id: string;
  name: string;
  url: string;
  size: number;
  type: string;
};

export type Stage =
  | 'pre_contracting'
  | 'finance_call'
  | 'contracting'
  | 'funding'
  | 'funded'
  | 'none';

export const Stages: Stage[] = [
  'pre_contracting',
  'finance_call',
  'contracting',
  'funding',
  'funded',
  'none',
];

export type PaymentMethod =
  | 'wire_transfer'
  | 'personal_check'
  | 'cash'
  | 'credit_card'
  | 'debit_card'
  | 'cashiers_check'
  | 'ach_plaid'
  | 'ach_other';

export type PaymentType = 'down_payment' | 'full_amount' | 'funding_amount';

export type Payment = {
  id: string;
  deal_id: string;
  amount: number;
  intent_date: string;
  sent_date: string | null;
  received_date: string | null;
  confirmed_date: string | null;
  refunded_date: string | null;
  receipt_uploaded_by_id: string | null;
  receipt_file_url: string | null;
  receipt_file_name: string | null;
  receipt_file_type: string | null;
  receipt_file_size: number | null;
  method: PaymentMethod;
  type: PaymentType;
  is_full_payment: boolean;
  created_by_id: User['id'];
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
};

export type ProtectionPlan = {
  id: string;
  deal_id: string;
  name: string;
  key: string;
  type: string;
  term_length: number;
  term_miles: number;
  monthly_price: number;
  price: number;
};

export type DealListItem = {
  id: string;
  stage: Stage;
  status: Status;
  priority: Priority;
  short_id: string;
  created_at: string;
  archived_at: string | null;
  customer_name: string | null;
  customer_email: string | null;
  co_registrant_name: string | null;
  finance_manager: UserRegistry | null;
  vehicle: VehicleListingRegistry | null;
  delivery_date: string;
  pickup_date: string | null;
  date_added_to_customer_delivery_queue: string | null;
  assigned_tasks: AssignedTaskRegistry[];
  payments: Payment[];
  trades: Trade[];
  primary_sales_rep: UserRegistry | null;
  secondary_sales_rep: UserRegistry | null;
  delivery_preference: DeliveryPreference;
  rapid_recon_step: RapidReconStep | null;
  delivery_stage: DeliveryStage | null;
  vehicle_delivery_odometer: number | null;
};

export type DEALS_GROUPED_BY_STATUS = Record<
  Status,
  {
    status: Status;
    count: number;
    deals: DealListItem[];
  }
>;

export type DEALS_GROUPED_BY_STAGE = Record<
  Stage,
  {
    stage: Stage;
    count: number;
    deals: DealListItem[];
  }
>;

export type DEALS_GROUPED_BY_DELIVERY_TYPE = Record<
  DeliveryPreference,
  {
    delivery_preference: DeliveryPreference;
    count: number;
    deals: DealListItem[];
  }
>;

export type Notification = {
  id: string;
  deal_id: string;
  triggered_by_user_id: string; // The user who triggered the notification
  user_id: string; // Apparently this is the user being mentioned
  message: string;
  read_at?: string | null;
  type: 'assignment' | 'mention';
  // TODO (jimmy): There's a better way to type this so that we know
  // which `metadata` keys are valid for a given `type`
  created_at: string;
  metadata?: {
    comment_id?: string;
    reply_id?: string;
  };
};

// {
//   "id": "7193eecb-fc79-42b7-b022-5bdec5c92cd5", // deal id
//   "user_id": "16c65e8c-43b8-42ac-8844-d86d2fe64f62", // user id
//   "short_id": "SFO-679", // deal short id
//   "hub_id": "8022034f-b02c-418a-95f1-4d7c4c9b8839" // hub id
// }
export type Favorite = {
  id: string;
  deal_id: string;
  user_id: string;
  hub_id: string;
};

export type UserFavorite = {
  deal_id: string;
  short_id: string;
  full_name: string;
  status: Status;
};

export type TradeStatus =
  | 'evaluation'
  | 'pending' // Pending is the buying team has put a number on the car for what we will pay for it but it has not been accepted by the customer yet.
  | 'accepted'
  | 'inbound' // Inbound would mean the car is on its way too us.
  | 'received' // Received means it’s landed at hq/auction
  | 'completed'
  | 'rejected';

export const TRADE_STATUSES: TradeStatus[] = [
  'evaluation',
  'pending',
  'accepted',
  'inbound',
  'received',
  'completed',
  'rejected',
];

export type Auction = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
};

export type VehicleLocation = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
};

export type VehicleListingStatus = 'ACTIVE' | 'HIDDEN';

export type VehicleListingPurchaseStatus =
  | 'AVAILABLE'
  | 'PURCHASE_IN_PROGRESS'
  | 'SOLD';

export const PurchaseStatuses: VehicleListingPurchaseStatus[] = [
  'AVAILABLE',
  'PURCHASE_IN_PROGRESS',
  'SOLD',
];

export type WholesaleOptionValue = 'WHOLESALE' | 'RETAIL';

export type VehicleHistory = 'No Accidents' | '1+ Accidents';

export type VehicleBody = 'Sedan' | 'Hatchback' | 'SUV' | 'Truck' | 'Coupe';

export type VehicleChargingPlug =
  | 'None'
  | 'Tesla NACS'
  | 'CHAdeMO and J1772'
  | 'J1772 and CCS1'
  | 'J1772';

export type VehicleListing = {
  id: string;
  vehicle_id: string;
  created_at: string;
  updated_at: string;
  status: VehicleListingStatus;
  purchase_status: VehicleListingPurchaseStatus;
  price: number;
  mileage: number;
  num_of_keys: number; // min 1, max 5
  vehicle_history: VehicleHistory;
  previous_owners: number;
  is_pre_order: boolean;
  transaction_type: WholesaleOptionValue;
  is_listed_wholesale: boolean;
  remaining_battery_warranty_miles: number;
  remaining_battery_warranty_months: number;
  images: VehicleImage[];
};

export type VehicleImage = {
  id: string;
  vehicle_listing_id: string;
  image_url: string;
  featured: boolean;
  blur_hash_img: string;
  status: string;
  order_index: number;
  timestamp: number;
  category: VehicleImageCategory;
};

export type VehicleImageCategory = {
  value: string;
  label: string;
};

// This type mainly exist for the purpose of auto-category assignment feature where we need
// to know category value ahead, but we usually should rely on available categories returned from API.
export enum VehicleImageCategoryEnum {
  FrontSeats = 'FRONT_SEATS',
  RearSeats = 'REAR_SEATS',
  Infotainment = 'INFOTAINMENT',
  CargoSpace = 'CARGO_SPACE',
  Wheels = 'WHEELS',
  PlugType = 'PLUG_TYPE',
  Other = 'OTHER',
}

export type VehicleSeatingCapacity = 0 | 2 | 4 | 5 | 6 | 7;

export type VehicleFromIndexer = {
  id: string;
  created_at: string;
  updated_at: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  exterior_color: string;
  interior_color: string;
  body: VehicleBody;
  charging_plug: VehicleChargingPlug;
  battery_capacity: number;
  epa_range: number;
  autopilot_features: string;
  top_features: string;
  seating_capacity: VehicleSeatingCapacity;
};

export type VEHICLE_LISTINGS_GROUPED_BY_PURCHASE_STATUS = Record<
  VehicleListingPurchaseStatus,
  {
    purchase_status: VehicleListingPurchaseStatus;
    count: number;
    vehicles: VehicleListingRegistry[];
  }
>;

export type SMSAgentMessage = {
  id: string;
  deal_id: string;
  message_id: number;
  message_date: string;
  message_status: string;
  direction: 'inbound' | 'outbound';
  to_number: string;
  from_number: string;
  text: string;
  mms: boolean;
  mms_url: string | null;
};

export type Seller = {
  id: string | number; // TODO -- shouldnt be doing this, but BE stores ids as int and FE interfaces use string. fix later
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  phone_number?: string;
  email?: string;
};

// Lead types
export type CustomerLead = {
  id: string;
  customer_id: number;
  inquiry_count: number;
  inquiries?: ConsolidatedInquiry[];
  personal_details?: PersonalDetails;
  trade_vehicles?: GenericVehicle[];
  has_ever_account: boolean;
  ever_account?: EverUser;
  previous_reservations?: PreviousReservation[];
  key_indicators?: KeyIndicators;
  original_sales_rep_id?: string;
  daily_completion_status?: string;
  priority: number;
  index?: number;
};

export type PreviousReservation = {
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  trim: string;
};

export type KeyIndicators = {
  multiple_lead_submissions: Indicator;
  full_contact_details_provided: Indicator;
  ever_account_holder: Indicator;
  multiple_vehicles_of_interest: Indicator;
  multiple_sources: Indicator;
  available_matching_inventory: Indicator;
  has_reservation_history: Indicator;
};

export type Indicator = {
  satisfied: boolean;
  weight: number;
  description: string;
};

export type ConsolidatedInquiry = {
  lead_id: number;
  inquiry_time: string;
  source: string;
  vehicles_of_interest?: GenericVehicle[];
};

export type PersonalDetails = {
  title?: string;
  first_name?: string;
  last_name?: string;
  nick_name?: string;
  company_name?: string;
  company_type?: string;
  addresses?: ConsolidatedCustomerAddress[];
  emails?: string[];
  phones?: string[];
};

export type ConsolidatedCustomerAddress = {
  address_id?: number;
  address_type?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  county?: string;
  state?: string;
  postal_code?: string;
};

export type GenericVehicle = {
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  vin?: string;
  stock_number?: string;
  similar_matches?: (GenericVehicle | undefined)[];
};

export type SalesRepresentative = {
  first_name?: string;
  last_name?: string;
  email?: string;
};

export type EverUser = {
  first_name?: string;
  last_name?: string;
  email?: string;
};

export type ProtectionPlanName = {
  id: string;
  name: string;
};

export type ProtectionPlanType = {
  id: string;
  type: string;
};

export type ProtectionPlanTermLength = {
  id: string;
  term_length: number;
};

export type ProtectionPlanTermMiles = {
  id: string;
  term_miles: number;
};

// V2 Types with UUID IDs
export type AppraisalV2 = {
  id: string; // UUID only
  tracking_id: number; // legacy auto-increment ID
  product_user_id: string | number;
  deal_id?: string;
  product_marketplace_listing_id?: number;
  vin: string;
  make: string;
  model: string;
  year: number;
  zip_code: string;
  phone_number: string;
  seller_name?: string;
  email?: string;
  drivetrain: string;
  trim: string;
  body: string;
  mileage: number;
  exterior_color: string;
  interior_color: string;
  seating_capacity: number;
  num_of_keys: number;
  condition: string;
  num_of_accidents: number;
  accident_type: string;
  exterior_damage_types: string[];
  exterior_damage_description?: string | null;
  interior_damage_types: string[];
  interior_damage_description?: string | null;
  mechanical_damage_types: string[];
  mechanical_damage_description?: string | null;
  self_driving_capability: string;
  highlights: string;
  status: string;
  is_upgrade_or_purchase_interested?: boolean;
  upgrade_or_purchase_interested_info?: string;
  debt_type?: string;
  tires_condition?: string; // enum: TireCondition
  exterior_panel_damage_count?: number;
  creation_step?: string; // enum: AppraisalCreationStep
  appraisal_origin_id?: string;
  is_serviced_regularly?: boolean;
  is_contacted_by_sales: boolean;
  sell_by?: string | null; // ISO date the seller wants to sell by
  is_just_browsing?: boolean | null; // seller is only checking, no sell intent
  source?: string | null;
  metadata?: AppraisalMetadata | null;
  created_at: string;
  updated_at: string;
};

export type RecurrentData = {
  range_score?: number | null;
  state_of_health_estimate?: number | null;
  last_connected_to_recurrent_at?: string | null;
  vehicle_details_url?: string | null;
};

export type AppraisalMetadata = {
  recurrent_data?: RecurrentData;
  drive_train?: string | null;
  condition?: string | null;
  suspension_modifications?: boolean | null;
  upgraded_wheels?: boolean | null;
  software_upgrades?: boolean | null;
  additional_notes?: string | null;
};

export type AppraisalOfferV2 = {
  id: string; // UUID
  tracking_id: number;
  offer_type: string;
  offer_amount_dollars: number;
  appraisal_v2_id: string; // UUID foreign key (was appraisal_id)
  status: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
};

export type InstantOfferV2 = {
  id: string; // UUID
  tracking_id: number;
  channel: string;
  channel_id: string; // appraisal tracking_id as string
  state: string;
  status: string;
  offer_type: string;
  powertrain_type: string;
  margin: number;
  risk_adjustment: number;
  risk_level: string;
  max_bid_price: number | null;
  start_bid_price: number | null;
  sales_price_estimate: number | null;
  sales_price_estimate_source: string | null;
  is_upgrade_or_purchase_interested: boolean | null;
};
